import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface PaymentStatus {
  subscription_status: 'active' | 'canceled' | 'past_due' | null;
  subscription_ends_at: string | null; // ISO timestamp
  trial_used: boolean;
  firm_subscription_status: string | null;
  /**
   * Whether this user holds one of the firm's paid seats. An active firm
   * subscription is not by itself access — a firm admin who runs no projects
   * is free and holds no seat. Read together with firm_subscription_status,
   * never on its own.
   */
  firm_has_seat: boolean;
  has_saved_payment_method: boolean;
  card_last4: string | null;
  card_brand: string | null;
  loading: boolean;
  error: Error | null;
}

const safeDefault: PaymentStatus = {
  subscription_status: null,
  subscription_ends_at: null,
  trial_used: false,
  firm_subscription_status: null,
  firm_has_seat: false,
  has_saved_payment_method: false,
  card_last4: null,
  card_brand: null,
  loading: true,
  error: null,
};

export function usePaymentStatus(refreshKey = 0): PaymentStatus {
  const [status, setStatus] = useState<PaymentStatus>(safeDefault);

  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      setStatus(prev => ({ ...prev, loading: true, error: null }));

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          if (!cancelled) setStatus({ ...safeDefault, loading: false });
          return;
        }

        const res = await fetch('/.netlify/functions/get-payment-status', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        if (!cancelled) {
          setStatus({
            subscription_status: data.subscription_status ?? null,
            subscription_ends_at: data.subscription_ends_at ?? null,
            trial_used: data.trial_used ?? false,
            firm_subscription_status: data.firm_subscription_status ?? null,
            firm_has_seat: data.firm_has_seat === true,
            has_saved_payment_method: data.has_saved_payment_method ?? false,
            card_last4: data.card_last4 ?? null,
            card_brand: data.card_brand ?? null,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        // Fail safe: never grant access on error — return null/false for all fields.
        if (!cancelled) {
          setStatus({
            subscription_status: null,
            subscription_ends_at: null,
            trial_used: false,
            firm_subscription_status: null,
            firm_has_seat: false,
            has_saved_payment_method: false,
            card_last4: null,
            card_brand: null,
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      }
    }

    loadStatus();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return status;
}
